import { type ButtonHTMLAttributes, type ReactNode, type Ref } from 'react';
import clsx from 'clsx';
import './PairButton.css';

export type PairButtonState = 'idle' | 'selected' | 'wrong' | 'matched';

export interface PairButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  children: ReactNode;
  /** Visual state driven by the TapPairs exercise engine. */
  state?: PairButtonState;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * PairButton — a cell in the `tap_pairs` exercise grid. Full width of its
 * column, 3D press physics matching the Button family. Four visual states:
 *   - idle     — default
 *   - selected — info-blue highlight (matches the selected state from Cluster 3)
 *   - wrong    — red shake animation after a mismatched pair
 *   - matched  — dimmed, disabled, primary-green tint (stays on the board)
 *
 * Colors come from the semantic token palette so dark mode adapts.
 */
export function PairButton({
  children,
  state = 'idle',
  type,
  className,
  disabled,
  ref,
  ...rest
}: PairButtonProps) {
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={clsx('fluent-pair', className)}
      data-state={state}
      disabled={disabled}
      aria-pressed={state === 'selected' || undefined}
      {...rest}
    >
      {children}
    </button>
  );
}
